/**
 * @file     useSupabaseUser.ts
 * @purpose  Client-side hook returning the current Supabase user (or null when anonymous) plus a loading flag. Subscribes to auth state changes so callers re-render on sign-in/out.
 */
'use client';

import { useEffect, useState } from 'react';
import type { AuthChangeEvent, Session, SupabaseClient, User } from '@supabase/supabase-js';
import { getSupabaseBrowserClient } from '@/core/supabase/client';
import { reportSilentFailure } from '@/core/observability/reportSilentFailure';

export interface SupabaseUserState {
  user: User | null;
  loading: boolean;
}

export function useSupabaseUser(): SupabaseUserState {
  const [state, setState] = useState<SupabaseUserState>({ user: null, loading: true });

  useEffect(() => {
    let active = true;
    let unsubscribe: (() => void) | null = null;

    // Loaded on demand (shared singleton) so the navbar and every page that
    // reads the user do not put @supabase/ssr in the first-load bundle.
    void getSupabaseBrowserClient().then((client: SupabaseClient | null) => {
      if (!active) return;
      if (!client) {
        setState({ user: null, loading: false });
        return;
      }

      client.auth.getUser()
        .then(({ data }) => {
          if (!active) return;
          setState({ user: data.user ?? null, loading: false });
        })
        .catch((err) => {
          if (!active) return;
          reportSilentFailure(err, { area: 'auth/getUser' });
          setState({ user: null, loading: false });
        });

      const { data: sub } = client.auth.onAuthStateChange((_event: AuthChangeEvent, session: Session | null) => {
        if (!active) return;
        setState({ user: session?.user ?? null, loading: false });
      });
      unsubscribe = () => sub.subscription.unsubscribe();
    });

    return () => {
      active = false;
      unsubscribe?.();
    };
  }, []);

  return state;
}
